import React, { useEffect } from "react";

import { isEmpty } from "lodash/fp";
import { useSearchParams } from "react-router-dom";
import { shallow } from "zustand/shallow";

import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import HeaderButton from "@app/analytics/components/reports/HeaderButton";
import useTopicReport from "@app/analytics/TopicReport/hooks/useTopicReport";
import useTopicReportImpersonations from "@app/analytics/TopicReport/hooks/useTopicReportImpersonations";
import useTopicReportStore from "@app/analytics/TopicReport/hooks/useTopicReportStore";
import { ShareIcon } from "@app/common/assets/icons";

import ShareTopicReportDialog from "./ShareTopicReportDialog";

const CountChip = styled(Typography)(({ theme }) => ({
  ...theme.typography.bodySecondaryMed,
  minWidth: "22px",
  padding: theme.spacing(0, 1),
  backgroundColor: theme.palette.transparent.white[20],
  borderRadius: "100px",
  // the button applies a font size to the end icon; override it
  fontSize: `${theme.typography.bodySecondaryMed.fontSize} !important`,
}));

interface ShareTopicReportProps {
  initEntityEmail: string | null;
}

const ShareTopicReport = ({ initEntityEmail }: ShareTopicReportProps) => {
  const [queryParams] = useSearchParams();
  const { impersonations } = useTopicReportImpersonations();
  const { topic, isLoading } = useTopicReport();
  const [shareDialogOpen, setShareDialogOpen] = useTopicReportStore(
    (state) => [state.shareDialogOpen, state.setShareDialogOpen],
    shallow
  );

  useEffect(() => {
    if (isLoading) return;
    if (
      queryParams.get("action") === "share" ||
      queryParams.get("shareEmail")
    ) {
      setShareDialogOpen(true);
    }
  }, [isLoading]);

  return (
    <>
      <HeaderButton
        primary
        endIcon={
          isEmpty(impersonations) ? (
            <ShareIcon />
          ) : (
            <CountChip>{impersonations.length}</CountChip>
          )
        }
        onClick={() => setShareDialogOpen(true)}
      >
        Share
      </HeaderButton>
      {topic && (
        <ShareTopicReportDialog
          onClose={() => setShareDialogOpen(false)}
          open={shareDialogOpen}
          topic={topic}
          initEntityEmail={initEntityEmail}
        />
      )}
    </>
  );
};

export default ShareTopicReport;
